import { FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { CashierPath } from '@ps/web-pam-types';
import { useNavigate, usePostMessage } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import useOpenDocumentVerification from '@swf/core/hooks/useOpenDocumentVerification';
import useOpenWindow from '@swf/core/hooks/useOpenWindow';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { select } from '@swf/core/store';

import useJumpBackAndResetDeposit from '../../../../../../hooks/useJumpBackAndResetDeposit';
import { DepositStatus } from '../../../../../../types/status';
import { getPaymentVerificationPath } from '../../../../../../utils/cashier';
import ButtonGroup from '../../../../../ButtonGroup/ButtonGroup';
import CTAButton from '../../../../../CTAButton/CTAButton';
import usePaymentMethodVerification from '../../../../hooks/usePaymentMethodVerification';

import {
  TXTCLI_Verify_account,
  TXTWCL_Back_to_homepage,
  TXTWCL_Contact_customer_support,
  TXTWCL_Deposit,
  TXTWCL_LetXs_play_1,
  TXTWCL_Return_home,
  TXTWCL_Return_to_Cashier,
  TXTWCL_Transaction_history,
  TXTWCL_Try_another_payment_method,
  TXTWCL_Upload_ID,
  TXTWCL_Upload_later,
  TXTWCL_Verify_payment_method
} from './DepositResultCTA.messages.json';

interface Props {
  status: DepositStatus;
  isBankTransfer: boolean;
  isPayconiqTimeout?: boolean;
  onClick?: () => void;
}

const DepositResultCTA: React.FC<Props> = ({
  status,
  isBankTransfer,
  isPayconiqTimeout,
  onClick
}) => {
  const navigate = useNavigate();
  const exitWizard = useWizardExit();
  const jumpBackAndResetDeposit = useJumpBackAndResetDeposit();
  const isLimitedAccount = useSelector(select.account.isLimitedAccount);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const userId = useSelector(select.account.getUserId);
  const email = useSelector(select.account.getEmail);
  const locale = useSelector(select.locale.get);
  const origin = useSelector(select.location.getOrigin);
  const postMessage = usePostMessage();
  const aliases = useSelector(select.cms.getAliases);
  const redirectTo = useSelector(select.cms.getRedirectUrl);

  const { data } = useWizardContext();
  const verifyDoc: boolean = useFeature(FeatureFlag.VerifyDoc);
  const isPaymentMethodVerificationRequired = usePaymentMethodVerification();
  const wizardJump = useWizardJump();
  const jumpToPaymentVerification = () => {
    wizardJump.jumpForward(getPaymentVerificationPath(data.cashierFlow));
  };

  const openContactSupport = useOpenDocumentVerification({
    resolverTag: ResolverTag.SwcSubmitDocumentsVerifications,
    query: { language: locale, userId, email },
    centered: true
  });
  const openRestrictedAccountSupport = useOpenWindow({
    resolverTag: ResolverTag.SwcContactSupportRestrictedAccount,
    query: { language: locale, userId, email },
    centered: true
  });

  switch (status) {
    case DepositStatus.AUTHENTICATION_REQUIRED:
      return (
        <ButtonGroup>
          <CTAButton
            type="button"
            translation={TXTWCL_Upload_ID}
            onClick={() => {
              exitWizard();
              openContactSupport();
            }}
          />
          <CTAButton
            type="button"
            onClick={exitWizard}
            translation={TXTWCL_Upload_later}
            variant="secondary"
          />
        </ButtonGroup>
      );
    case DepositStatus.COMPLETED:
      return isBankTransfer ? (
        <ButtonGroup>
          <CTAButton type="button" onClick={exitWizard} translation={TXTWCL_Back_to_homepage} />
          <CTAButton
            type="button"
            variant="secondary"
            alias="firstWalletHome"
            translation={TXTWCL_Return_to_Cashier}
          />
        </ButtonGroup>
      ) : (
        <ButtonGroup>
          <CTAButton
            translation={isLimitedAccount ? TXTCLI_Verify_account : TXTWCL_LetXs_play_1}
            type="button"
            onClick={() => {
              switch (true) {
                case isLimitedAccount && isEmbedded && verifyDoc:
                  window.starsweb?.app?.execute('docverify');
                  break;
                case isLimitedAccount && isEmbedded && !verifyDoc:
                  navigate('/embedded/modal/auth/docverify');
                  break;
                case isLimitedAccount && !isEmbedded:
                  navigate('/docverify');
                  break;
                case isEmbedded && redirectTo === aliases.firstWalletHome:
                  postMessage('close');
                  window.close();
                  break;
                case redirectTo === aliases.firstWalletHome:
                  navigate('/');
                  break;
                case !!onClick:
                  onClick && onClick();
                  break;
                default:
                  exitWizard();
              }
            }}
          />
          {isPaymentMethodVerificationRequired ? (
            <CTAButton
              type="button"
              variant="secondary"
              translation={TXTWCL_Verify_payment_method}
              onClick={jumpToPaymentVerification}
            />
          ) : (
            <CTAButton
              type="button"
              variant="secondary"
              alias="firstWalletHome"
              translation={TXTWCL_Return_to_Cashier}
            />
          )}
        </ButtonGroup>
      );
    case DepositStatus.ERRORS:
    case DepositStatus.REJECTED_CARD_BLOCKED:
      return (
        <ButtonGroup>
          <CTAButton
            type="button"
            onClick={openRestrictedAccountSupport}
            translation={TXTWCL_Contact_customer_support}
          />
          <CTAButton
            type="button"
            variant="secondary"
            onClick={exitWizard}
            translation={TXTWCL_Return_home}
          />
        </ButtonGroup>
      );
    case DepositStatus.TIMEOUT:
      return isPayconiqTimeout ? (
        <ButtonGroup>
          <CTAButton
            type="button"
            href={`${origin}/${locale}${CashierPath.Deposit}`}
            translation={TXTWCL_Deposit}
          />
          <CTAButton
            type="button"
            variant="secondary"
            alias="firstWalletHome"
            translation={TXTWCL_Return_to_Cashier}
          />
        </ButtonGroup>
      ) : (
        <CTAButton type="button" onClick={exitWizard} translation={TXTWCL_Return_home} />
      );

    case DepositStatus.GOOGLE_PAY_CANCELED:
    case DepositStatus.GOOGLE_PAY_FAILED:
    case DepositStatus.GOOGLE_PAY_SESSION_TIMEOUT:
    case DepositStatus.REJECTED:
      return (
        <CTAButton
          href={`${origin}/${locale}${CashierPath.Deposit}`}
          translation={TXTWCL_Try_another_payment_method}
        />
      );

    case DepositStatus.REJECTED_MAX_DEPOSITS_LIMIT_EXCEEDED:
      return (
        <CTAButton
          type="button"
          onClick={() => {
            data.depositResult = null;
            jumpBackAndResetDeposit('#deposit.deposit-method');
          }}
          translation={TXTWCL_Try_another_payment_method}
        />
      );
    case DepositStatus.UNKNOWN_TRANSACTION_ID:
    case DepositStatus.REJECTED_PEP_HIO_THIRD_PARTY:
      return <CTAButton type="button" onClick={exitWizard} translation={TXTWCL_Return_home} />;
    case DepositStatus.ERR_LUGAS_PLAYER_IS_NOT_REGISTERED:
      return (
        <CTAButton type="button" onClick={exitWizard} translation={TXTWCL_Return_to_Cashier} />
      );
    case DepositStatus.MISSING_TRANSACTION_ID:
      return (
        <ButtonGroup>
          <CTAButton
            type="button"
            translation={TXTWCL_Transaction_history}
            onClick={() => navigate(CashierPath.TransactionHistory)}
          />
          <CTAButton
            type="button"
            variant="secondary"
            alias="firstWalletHome"
            translation={TXTWCL_Return_to_Cashier}
          />
        </ButtonGroup>
      );
    default:
      return <CTAButton type="button" onClick={exitWizard} translation={TXTWCL_Return_home} />;
  }
};

export default DepositResultCTA;
